<template>
    <div
        ref="nedglobePreviewPanel"
        :class="[
            mode === 'preview' ? 'preview-pane' : '',
            userSettings.previewzoom > 100 ? 'snap-left' : ''
        ]"
    >
        <div :class="[previewAreaSize ? '' : 'init', 'print-map']">
            <div v-show="isLoadingTiles" class="loader">
                <div class="loader-panel">
                    <b-spinner label="Spinning"></b-spinner>
                </div>
            </div>
            <div
                v-show="previewAreaSize && previewConfig"
                :style="paperSizeStyle"
                :class="[mode === 'preview' ? 'print-page-preview' : '']"
            >
                <div
                    ref="nedglobePreview"
                    class="print-pane"
                    :style="paperSizeScaleStyle"
                >
                    <div class="page-margin" :style="paperMarginStyle"></div>
                    <div class="page-elements" :style="paperMarginStyle">
                        <div
                            ref="nedglobeMapPanel"
                            class="print-overlay print-map"
                            :class="{ 'is-mobile-device': isMobileDevice }"
                            :style="previewConfig.map.css.parent"
                        >
                            <div
                                ref="nedglobeMapElement"
                                id="nedglobe.print.map"
                                :data-scale="displayScale"
                                :style="previewConfig.map.css.child"
                            ></div>
                        </div>
                        <div
                            v-if="userSettings.showLegend"
                            ref="legendPanel"
                            class="print-overlay legend"
                            :style="previewConfig.legend.css.parent"
                        >
                            <div
                                :style="previewConfig.legend.css.child"
                                ref="legendElement"
                            >
                                <print-legend />
                            </div>
                        </div>
                        <div
                            v-if="
                                userSettings.showScale &&
                                previewConfig.scale &&
                                userSettings.scale
                            "
                            ref="scalePanel"
                            class="print-overlay scale"
                            :style="previewConfig.scale.css.parent"
                        >
                            <div
                                :style="previewConfig.scale.css.child"
                                ref="scaleElement"
                            >
                                <print-map-scale
                                    :metersPerPixel="resolutionMeters"
                                    :width="previewConfig.scale.width"
                                    :scale="currentScale"
                                />
                            </div>
                        </div>
                        <div
                            v-if="userSettings.showNorth"
                            ref="northPanel"
                            class="print-overlay north"
                            :style="previewConfig.north.css.parent"
                        >
                            <div
                                :style="previewConfig.north.css.child"
                                ref="northElement"
                            >
                                <img
                                    :style="imageStyle"
                                    :src="$_getNorthArrow()"
                                    alt="north"
                                />
                            </div>
                        </div>
                        <div
                            v-if="userSettings.showLogo && logo"
                            ref="logoPanel"
                            class="print-overlay logo"
                            :style="previewConfig.logo.css.parent"
                        >
                            <div
                                :style="previewConfig.logo.css.child"
                                ref="logoElement"
                            >
                                <img :src="logo" alt="logo" />
                            </div>
                        </div>
                        <div
                            v-if="userSettings.showFreetext"
                            ref="freetextPanel"
                            class="print-overlay freetext"
                            :style="previewConfig.freetext.css.parent"
                        >
                            <div
                                :style="previewConfig.freetext.css.child"
                                ref="freetextElement"
                            >
                                {{ userSettings.freetext }}
                            </div>
                        </div>
                        <div
                            v-show="userSettings.showTitle"
                            ref="titlePanel"
                            class="print-overlay title"
                            :style="previewConfig.title.css.parent"
                        >
                            <div
                                :style="previewConfig.title.css.child"
                                ref="titleElement"
                            >
                                {{ userSettings.title }}
                            </div>
                        </div>
                        <div
                            v-if="userSettings.showDate"
                            ref="datePanel"
                            class="print-overlay date"
                            :style="previewConfig.date.css.parent"
                        >
                            <div
                                :style="previewConfig.date.css.child"
                                ref="dateElement"
                            >
                                {{
                                    new Date().toLocaleDateString(
                                        this.getLocale
                                    )
                                }}
                            </div>
                        </div>
                        <div
                            v-if="userSettings.showDisclaimer"
                            ref="disclaimerPanel"
                            class="print-overlay disclaimer"
                            :style="previewConfig.disclaimer.css.parent"
                        >
                            <div
                                :style="previewConfig.disclaimer.css.child"
                                ref="disclaimerElement"
                            >
                                {{ previewConfig.disclaimer.content.text }}
                            </div>
                        </div>
                        <div
                            ref="copyrightPanel"
                            class="print-overlay copyright"
                            :style="previewConfig.copyright.css.parent"
                        >
                            <div
                                :style="previewConfig.copyright.css.child"
                                ref="copyrightElement"
                            >
                                {{ mapTerms }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import PrintMapScale from './PrintMapScale.vue';
    import PrintLegend from './PrintLegend.vue';
    import { isMobileDevice } from '../../services/utilities/devices';
    import {
        getResolutionFromScale,
        getScaleFromResolution,
        isInRange
    } from '../../services/map';
    import { DEFAULT_DOTS_PER_INCH } from '../../constants';
    import {
        renderPrintMap,
        renderLayers,
        pixelsToMM
    } from '../../services/printing/printDuplicateMap';
    import {
        isInFilteredList,
        getNextId
    } from '../../services/utilities/baseLayer';

    export default {
        name: 'PrintMap',
        components: {
            'print-map-scale': PrintMapScale,
            'print-legend': PrintLegend
        },
        props: {
            dpi: { type: Number, required: false, default: 180 },
            previewConfig: { type: Object, required: true },
            mode: { type: String, required: true },
            userSettings: { type: Object, required: true },
            originalMap: { type: Object, required: true },
            logo: { type: String, required: true },
            northarrow: { type: String, required: true }
        },
        data() {
            return {
                map: null,
                resolution: null,
                previewAreaSize: null,
                originalSize: null,
                originalResolution: null,
                displayScale: 1,
                wmsHighResolution: false,
                loadMonitor: { loaded: 0, start: 0, error: 0 },
                isMobileDevice: isMobileDevice(),
                baseLayer: null,
                filteredBaseLayers: [],
                previousResolution: 0,
                rotation: 0,
                // built in default north arrow
                northUrl: require('@/assets/icons/noordpijl.png')
            };
        },

        mounted() {
            this.$_onStart();
        },
        methods: {
            async $_onStart() {
                this.previewAreaSize = {
                    width: this.$refs.nedglobePreviewPanel.clientWidth,
                    height: this.$refs.nedglobePreviewPanel.clientHeight
                };
                this.baseLayer = this.activeBaseLayer;
                await this.initializeMap();
                if (this.mode === 'print') {
                    this.$nextTick(() => {
                        this.map.once('rendercomplete', async () => {
                            this.$emit('startPrint');
                        });
                    });
                }
                this.previousResolution = this.resolution;
            },
            $_setMapSize() {
                if (!this.map) {
                    return this.$_updateMapSize();
                }
                if (!this.$refs.nedglobeMapElement) {
                    return undefined;
                }
                const scale =
                    this.mode === 'preview' ? this.displayScale : this.printScale;
                const { width, height } =
                    this.$refs.nedglobeMapElement.getBoundingClientRect();
                this.map.setSize([width / scale, height / scale]);
                this.resolution = this.originalResolution * this.scale;
                return undefined;
            },
            $_updateMapSize() {
                this.$nextTick(() => {
                    this.$_setMapSize();
                });
            },
            $_setPreviewScale() {
                if (this.mode === 'preview') {
                    const previewzoom = this.userSettings.previewzoom || 100;
                    this.wmsHighResolution = false;
                    this.displayScale = this.scale * (previewzoom / 100);
                } else {
                    this.wmsHighResolution = true;
                    this.displayScale = 1;
                }
            },
            $_setPreviewResolution() {
                if (!(this.userSettings.scale && this.map)) {
                    return;
                }
                const displayResolution = getResolutionFromScale(
                    this.map,
                    this.userSettings.scale
                );
                // Set resolution dependend background layer
                this.$_setBackgroundLayer(displayResolution);
                this.map.getView().setResolution(displayResolution);
                this.map.getView().setRotation(this.rotation);
            },
            // Set the background layer based on selected resolution
            $_setBackgroundLayer(resolution) {
                // build new list of base layers for current resolution
                this.filteredBaseLayers = this.$_filteredBaseLayers(resolution);

                // If no baselayers available for this resolution, remove the current baselayer.
                if (this.filteredBaseLayers.length == 0) {
                    if (this.baseLayer) {
                        this.$store.commit('REMOVE_BASE_LAYER_FROM_MAP', {
                            mapName: this.mapName
                        });
                    }
                    this.baseLayer = null;
                    return;
                }

                // Current baselayer is still in filteredlist. No action needed.
                if (isInFilteredList(this.baseLayer, this.filteredBaseLayers)) {
                    return;
                }

                // Find a new background layer.
                const nextId = getNextId(
                    this.baseLayers,
                    this.filteredBaseLayers,
                    this.baseLayer,
                    this.$_zoomDirection(resolution)
                );

                this.baseLayer = nextId;
                this.$_selectBaselayer(this.baseLayer);
            },

            // Determine whether we are zooming in or out.
            $_zoomDirection(resolution) {
                let direction = null;
                if (this.previousResolution < resolution) {
                    direction = 'out';
                } else if (this.previousResolution > resolution) {
                    direction = 'in';
                }
                // save last known resolution
                this.previousResolution = resolution;

                return direction;
            },

            $_filteredBaseLayers(resolution) {
                return this.baseLayers.filter((b) =>
                    isInRange(
                        resolution,
                        b.data.minBaseLayerResolution,
                        b.data.maxBaseLayerResolution
                    )
                );
            },

            $_selectBaselayer(id) {
                this.$store.dispatch('addLayerToMap', {
                    mapName: this.mapName,
                    id
                });
            },
            resetMap() {
                this.$nextTick(() => {
                    this.clearMap();
                    this.renderMapLayers();
                });
            },
            clearMap() {
                this.map.setTarget(null);
            },
            destroyPrintMaps() {
                this.$store.commit('UNREGISTER_MAP', { name: 'printMap' });
                this.$store.commit('UNREGISTER_MAP', { name: 'previewMap' });
            },
            async initializeMap() {
                const originalView = this.originalMap.getView();
                this.originalSize = this.originalMap.getSize();
                this.originalResolution = originalView.getResolution();
                this.resolution = this.originalResolution;
                this.rotation = originalView.getRotation();

                if (this.mode === 'print') {
                    this.rotation =
                        this.referenceMap.map.values_.view.values_.rotation;
                }
                await this.createMap();
                this.$_setPreviewResolution();
                this.$_setMapSize();
                await this.renderMapLayers();
            },
            async createMap() {
                this.map = await renderPrintMap(
                    this.$store,
                    this.mapName,
                    'nedglobe.print.map',
                    this.referenceMap,
                    this.wmsHighResolution,
                    this.loadMonitor
                );

                return this.map;
            },
            async renderMapLayers() {
                await renderLayers(
                    this.$store,
                    this.mapName,
                    this.referenceMap,
                    this.wmsHighResolution,
                    this.loadMonitor
                );
            },
            $_getNorthArrow() {
                // return either the organization nortarrow, or a built in default northarrow
                return this.northarrow ? this.northarrow : this.northUrl;
            }
        },
        watch: {
            mode(newValue) {
                this.$_setPreviewScale();
                if (newValue === 'print') {
                    this.resetMap();
                }
            },
            scale() {
                this.$_setPreviewScale();
            },
            loadMonitor: {
                handler: function () {
                    this.$emit('loading', this.isLoadingTiles);
                },
                deep: true
            },
            currentRotation(newValue) {
                this.rotation = newValue;
            },
            'userSettings.size': '$_updateMapSize',
            'userSettings.previewzoom': '$_setPreviewScale',
            'userSettings.scale': '$_setPreviewResolution'
        },
        computed: {
            ...mapState(['defaultMap']),
            ...mapGetters(['getLocale', 'getBaseLayers']),
            mapName() {
                return this.mode === 'print' ? 'printMap' : 'previewMap';
            },
            baseLayers: function () {
                return this.getBaseLayers;
            },
            activeBaseLayer() {
                return this.defaultMap?.baseLayer?.id;
            },
            isLoadingTiles: function () {
                return (
                    this.loadMonitor.start >
                    this.loadMonitor.loaded + this.loadMonitor.error
                );
            },
            referenceMap: function () {
                return this.$store.getters.getMapByName(
                    this.mode === 'print' ? 'previewMap' : undefined
                );
            },
            userIsAuthenticated: function () {
                return (
                    this.$store.getters.userIsAuthenticated ||
                    this.$store.state.requiresAuthentication
                );
            },
            mapTerms: function () {
                return this.$store.getters.getMapTerms().join('\n');
            },
            scale: function () {
                if (!(this.previewConfig && this.previewAreaSize)) {
                    return 1;
                }
                const rectRatio =
                    this.previewConfig.paper.width /
                    this.previewConfig.paper.height;
                const boundsRatio =
                    this.previewAreaSize.width / this.previewAreaSize.height;
                return rectRatio > boundsRatio
                    ? this.previewAreaSize.width / this.previewConfig.paper.width
                    : this.previewAreaSize.height / this.previewConfig.paper.height;
            },
            resolutionMeters: function () {
                if (!this.map) {
                    return 0;
                }
                const view = this.map.getView();
                return view.getResolution();
            },
            paperSizeStyle: function () {
                if (this.mode === 'preview') {
                    return {
                        width: `${
                            this.previewConfig.paper.width * this.displayScale
                        }px`,
                        height: `${
                            this.previewConfig.paper.height * this.displayScale
                        }px`
                    };
                }
                const props = this.previewConfig.paper._props;
                const height =
                    props.height - (props.margins.top + props.margins.bottom);
                const width =
                    props.width - (props.margins.left + props.margins.right);

                return {
                    top: `${props.margins.top}mm`,
                    left: `${props.margins.left}mm`,
                    width: `${width}mm`,
                    // -1 prevents extra page
                    height: `${height - 1}mm`,
                    overflow: 'hidden'
                };
            },
            printScale: function () {
                const a4wMM = this.previewConfig.paper._props.width;
                return (
                    a4wMM /
                    pixelsToMM(
                        this.previewConfig.paper.width,
                        DEFAULT_DOTS_PER_INCH
                    )
                );
            },
            paperSizeScaleStyle: function () {
                if (this.mode === 'preview') {
                    return {
                        width: `${this.previewConfig.paper.width}px`,
                        height: `${this.previewConfig.paper.height}px`,
                        transform: `scale(${this.displayScale})`
                    };
                }
                const scale = this.printScale;
                const props = this.previewConfig.paper._props;
                const height =
                    props.height - (props.margins.top + props.margins.bottom);
                const width =
                    props.width - (props.margins.left + props.margins.right);

                return {
                    width: `${width * (1 / scale)}mm`,
                    height: `${height * (1 / scale)}mm`,
                    transform: `scale(${scale})`
                };
            },
            paperMarginStyle: function () {
                if (this.mode === 'preview') {
                    return {
                        top: `${this.previewConfig.paper.top}px`,
                        left: `${this.previewConfig.paper.left}px`,
                        bottom: `${this.previewConfig.paper.bottom}px`,
                        right: `${this.previewConfig.paper.right}px`
                    };
                }
                return {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                };
            },
            imageStyle() {
                return {
                    transform: `rotate(${this.currentRotation}rad)`
                };
            },
            currentScale: function () {
                if (this.map) {
                    return getScaleFromResolution(
                        this.map,
                        this.map.getView().getResolution()
                    );
                } else {
                    return this.userSettings.scale ?? 0;
                }
            },
            currentRotation() {
                if (this.map) {
                    return this.map.getView().getRotation();
                } else {
                    return 0;
                }
            }
        }
    };
</script>

<style scoped lang="scss">
.loader {
    position: absolute;
    top: 20px;
    left: 0;
    z-index: 10;
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}
.loader-panel {
    background-color: white;
    border-radius: 100%;
}
.preview-pane {
    height: 80vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: visible;
}
.preview-pane.snap-left {
    overflow: auto;
}
.init {
    position: absolute;
    width: 100%;
    height: 100%;
}
.print-overlay {
    position: absolute;
    pointer-events: none;
    color-adjust: exact;
    -webkit-print-color-adjust: exact;
    * {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
    }
}
.print-overlay.copyright {
    white-space: pre-line;
}
.print-overlay.logo img {
    /* height of logo was too big: so 60% */
    max-width: 100%;
    max-height: 60%;
}
.print-overlay.north img {
    /* north arrow size 50% */
    max-width: 50%;
    max-height: 50%;
}
.print-map {
    pointer-events: all;

    & .is-mobile-device {
        pointer-events: none;
    }
}
.print-page-preview {
    position: relative;
    overflow: hidden;
    border: 2px solid black;
}
.page-margin {
    position: absolute;
    border: 2px dotted grey;
}
.page-elements {
    position: absolute;
}
.print-pane {
    position: absolute;
    transform-origin: left top;
    width: 100%;
    height: 100%;
}
.snap-left .print-map {
    align-self: start;
}

@media print {
    .print-map {
        .print-page-preview {
            border: none;
        }
    }
    .print-pane {
        position: relative;
    }
    .page-margin {
        border: none;
    }
    .loader {
        display: none;
    }
}
</style>
